import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { completeClioOAuth, completeClioOnboarding, createClioOnboardingAttempt, getClioIntegration, getClioOnboardingAttempt, listActiveWitnessTenants, type ActiveWitnessTenant, type ClioIntegration, type ClioOnboardingAttempt } from '../../lib/witness-api';
import { configureTenantAuth, hasActiveTenantSession } from '../../lib/tenant-auth';

const CLIO_ATTEMPT_KEY = 'witness.clio.onboarding';

export function ClioActionPlaceholderPage({ experience }: {
  experience: 'ceremony' | 'configuration';
}) {
  const name = experience === 'ceremony' ? 'Witness Ceremony' : 'Witness Configuration';
  return <main className="mx-auto max-w-xl px-6 py-16">
    <h1 className="font-serif text-3xl">{name}</h1>
    <p className="mt-4 text-slate-600">This Clio action is installed. Its Witness experience is coming next.</p>
  </main>;
}

export function TenantIntegrationsPage() {
  const { tenantId = '' } = useParams();
  const [attempt, setAttempt] = useState<ClioOnboardingAttempt | null>(null);
  const [integration, setIntegration] = useState<ClioIntegration | null>(null);

  useEffect(() => {
    getClioIntegration(tenantId).then(setIntegration).catch(() => undefined);
    const saved = readSavedAttempt();
    if (saved?.tenantId === tenantId) getClioOnboardingAttempt(tenantId, saved.attemptId).then(setAttempt).catch(() => undefined);
  }, [tenantId]);

  return <section className="space-y-6">
    <div><h1 className="text-2xl font-semibold">Integrations</h1><p className="mt-1 text-sm text-slate-600">Connect this Witness tenant to external systems.</p></div>
    <div className="max-w-2xl rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold">Clio Manage</h2>
      {integration ? <IntegrationStatus integration={integration} /> : attempt ? <AttemptStatus attempt={attempt} /> : <p className="mt-2 text-sm text-slate-600">Authorize your Clio user and resolve the Clio firm for this installation.</p>}
      {!integration && <Link className="mt-5 inline-block rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white"
        to={`/integrations/clio/connect?source=witness&tenant=${encodeURIComponent(tenantId)}`}>
        {attempt?.status === 'failed' ? 'Try again' : 'Connect Clio'}
      </Link>}
    </div>
  </section>;
}

export function ConnectClioPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const source = params.get('source') === 'clio' ? 'clio' : 'witness';
  const requestedTenantId = params.get('tenant');
  const [tenants, setTenants] = useState<readonly ActiveWitnessTenant[] | null>(null);
  const [tenantId, setTenantId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    configureTenantAuth().then(hasActiveTenantSession).then((active) => {
      if (!active) {
        const destination = `${window.location.pathname}${window.location.search}`;
        navigate(`/tenant/login?destination=${encodeURIComponent(destination)}`, { replace: true });
        return null;
      }
      return listActiveWitnessTenants();
    }).then((eligible) => {
      if (!eligible) return;
      setTenants(eligible);
      const requested = eligible.find((tenant) => tenant.id === requestedTenantId);
      if (requested) setTenantId(requested.id);
      else if (eligible.length === 1) setTenantId(eligible[0]!.id);
    }).catch(() => setError('Could not load your eligible Witness tenants.'));
  }, [navigate, requestedTenantId]);

  async function connect() {
    if (!tenantId) return;
    setBusy(true); setError(null);
    try {
      const created = await createClioOnboardingAttempt(tenantId, source);
      sessionStorage.setItem(CLIO_ATTEMPT_KEY, JSON.stringify({ tenantId, attemptId: created.attemptId }));
      window.location.assign(created.authorizationUrl);
    } catch { setError('Could not begin Clio authorization.'); setBusy(false); }
  }

  return <main className="mx-auto max-w-xl px-6 py-16">
    <h1 className="font-serif text-3xl">Connect Clio</h1>
    <p className="mt-3 text-sm text-slate-600">Choose the Witness tenant to pair with the Clio firm that you authorize next.</p>
    {tenants?.length === 0 && <p className="mt-6 text-sm text-red-700">You do not have an active Witness tenant membership.</p>}
    {tenants && tenants.length > 0 && <label className="mt-6 block text-sm font-medium">Witness tenant
      <select className="mt-2 block w-full rounded border border-slate-300 bg-white px-3 py-2" value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
        {tenants.length > 1 && <option value="">Select a tenant</option>}
        {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.displayName}</option>)}
      </select>
    </label>}
    {error && <p className="mt-4 text-sm text-red-700">{error}</p>}
    <button className="mt-6 rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      disabled={!tenantId || busy} onClick={connect}>{busy ? 'Opening Clio…' : 'Continue to Clio'}</button>
  </main>;
}

export function ClioCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  useEffect(() => {
    const saved = readSavedAttempt();
    const code = params.get('code'); const state = params.get('state');
    if (!saved || !code || !state) return;
    configureTenantAuth().then(() => completeClioOAuth({ tenantId: saved.tenantId, code, state })).then((attempt) => {
      navigate(statusUrl(saved.tenantId, attempt.id), { replace: true });
    }).catch(() => navigate(statusUrl(saved.tenantId, saved.attemptId), { replace: true }));
  }, [navigate, params]);
  const callbackComplete = readSavedAttempt() && params.get('code') && params.get('state');
  return <main className="mx-auto max-w-xl p-10"><h1 className="text-2xl font-semibold">Connect Clio</h1><p className="mt-4 text-slate-700">
    {callbackComplete ? 'Completing Clio authorization…' : 'This Clio callback is missing its saved attempt, code, or state.'}
  </p></main>;
}

export function ClioOnboardingStatusPage() {
  const { attemptId = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tenantId = params.get('tenant') ?? '';
  const [attempt, setAttempt] = useState<ClioOnboardingAttempt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [integration, setIntegration] = useState<ClioIntegration | null>(null);
  const [installing, setInstalling] = useState(false);
  const installationStarted = useRef(false);

  async function finishConnection() {
    if (!attemptId || !tenantId || installing) return;
    setError(null);
    setInstalling(true);
    try {
      setIntegration(await completeClioOnboarding(tenantId, attemptId));
      setAttempt((current) => current ? { ...current, status: 'complete', failure: null } : current);
    } catch {
      setError('Could not finish the Clio connection. Retry now or return later to repair it.');
      setAttempt((current) => current ? { ...current, status: 'failed' } : current);
    } finally { setInstalling(false); }
  }

  useEffect(() => {
    if (!attemptId || !tenantId) { setError('This onboarding status URL is incomplete.'); return; }
    configureTenantAuth().then(hasActiveTenantSession).then((active) => {
      if (!active) {
        const destination = `${window.location.pathname}${window.location.search}`;
        navigate(`/tenant/login?destination=${encodeURIComponent(destination)}`, { replace: true });
        return null;
      }
      return getClioOnboardingAttempt(tenantId, attemptId);
    }).then(async (loaded) => {
      if (!loaded) return;
      setAttempt(loaded);
      if (!['oauth_complete', 'configuring'].includes(loaded.status) || installationStarted.current) return;
      installationStarted.current = true;
      await finishConnection();
    })
      .catch(() => setError('Could not load this Clio onboarding attempt.'));
  }, [attemptId, navigate, tenantId]);

  return <main className="mx-auto max-w-xl px-6 py-16">
    <h1 className="font-serif text-3xl">Connect Clio</h1>
    {!attempt && !error && <p className="mt-4 text-slate-600">Loading onboarding status…</p>}
    {attempt && <div className="mt-6 rounded border border-slate-200 bg-white p-5">
      <AttemptStatus attempt={attempt} />
      {attempt.status === 'oauth_complete' && <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-slate-500">Clio user</dt><dd>{attempt.clioUserId}</dd>
        <dt className="text-slate-500">Clio tenant</dt><dd>{attempt.clioTenantId}</dd>
      </dl>}
      {installing && <p className="mt-5 text-sm text-amber-700">Finishing the Clio connection…</p>}
      {integration && <IntegrationStatus integration={integration} />}
    </div>}
    {error && <p className="mt-4 text-red-700">{error}</p>}
    {(error || attempt?.status === 'failed') && attempt?.clioTenantId && <button className="mt-3 rounded bg-slate-900 px-4 py-2 text-sm text-white"
      disabled={installing} onClick={finishConnection}>{installing ? 'Retrying…' : 'Retry'}</button>}
    <Link className="mt-6 inline-block text-sm underline" to={`/t/${encodeURIComponent(tenantId)}/integrations`}>Back to Integrations</Link>
  </main>;
}

function IntegrationStatus({ integration }: { integration: ClioIntegration }) {
  const installed = integration.clioFieldSetId !== null && integration.attributes.length === 4;
  return <p className={`mt-2 text-sm ${installed ? 'text-emerald-700' : 'text-amber-700'}`}>
    {integration.status === 'ready' ? `Clio tenant ${integration.clioTenantId} is connected and ready.` : installed ? `Clio tenant ${integration.clioTenantId} is paired and all four Matter fields are installed.` :
      `Clio tenant ${integration.clioTenantId} is paired; field installation can be resumed.`}
  </p>;
}

function AttemptStatus({ attempt }: { attempt: ClioOnboardingAttempt }) {
  if (attempt.status === 'oauth_complete') return <p className="mt-2 text-sm text-emerald-700">OAuth complete — Clio user {attempt.clioUserId}, tenant {attempt.clioTenantId}.</p>;
  if (attempt.status === 'configuring') return <p className="mt-2 text-sm text-amber-700">Finishing Clio configuration…</p>;
  if (attempt.status === 'complete') return <p className="mt-2 text-sm text-emerald-700">Clio connection complete.</p>;
  if (attempt.status === 'failed') return <p className="mt-2 text-sm text-red-700">
    {attempt.failure === 'clio_admin_required'
      ? 'Initial Clio setup must be authorized by a Clio administrator.'
      : `Connection failed (${attempt.failure ?? 'unknown'}).`}
  </p>;
  return <p className="mt-2 text-sm text-amber-700">Waiting for Clio authorization.</p>;
}

function readSavedAttempt(): { tenantId: string; attemptId: string } | null {
  try { return JSON.parse(sessionStorage.getItem(CLIO_ATTEMPT_KEY) ?? 'null') as { tenantId: string; attemptId: string } | null; }
  catch { return null; }
}

function statusUrl(tenantId: string, attemptId: string): string {
  return `/integrations/clio/onboarding/${encodeURIComponent(attemptId)}?tenant=${encodeURIComponent(tenantId)}`;
}
